import axios from 'axios';

import { createAsyncThunk } from '@reduxjs/toolkit';
import { authStoreKey } from './auth.const';
import { setSnackbar } from '@redux/snackbar';

import {
  SnackbarType,
  forgotPasswordAPI,
  resetPasswordAPI,
  userProfileAPI,
} from '@constants';
import { ILoginResponse, IProfile } from '@types';
import { auth } from 'services/firebase';
import {
  signInWithCustomToken,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { logEvent } from '@services';

export const customLoginUser = createAsyncThunk(
  `${authStoreKey}/customLoginUser`,
  async (customEmail: string, thunkAPI) => {
    try {
      const { data } = await axios.post('/auth/custom-login', {
        email: customEmail,
      });

      await signInWithCustomToken(auth, data);
    } catch (e: any) {
      thunkAPI.dispatch(
        setSnackbar({
          type: SnackbarType.Error,
          message: 'Failed to login with custom token:',
        }),
      );

      throw e;
    }
  },
);

export const loginUser = createAsyncThunk(
  `${authStoreKey}/loginUser`,
  async (_, thunkAPI) => {
    try {
      const response = await axios.get<IProfile>(userProfileAPI);

      return response.data;
    } catch (e: any) {
      if (e.response?.data?.statusCode === 401) {
        thunkAPI.dispatch(
          setSnackbar({
            type: SnackbarType.Error,
            message: 'Login failed on the server, please try again',
          }),
        );
      } else {
        thunkAPI.dispatch(
          setSnackbar({
            type: SnackbarType.Error,
            message:
              'Something went wrong with login, please contact developers',
          }),
        );
      }

      throw e;
    }
  },
);

export const logoutUser = createAsyncThunk(
  `${authStoreKey}/logoutUser`,
  async () => {
    await auth.signOut();
  },
);

export const forgotUserPassword = createAsyncThunk(
  `${authStoreKey}/forgotUserPassword`,
  async (username: string, thunkAPI) => {
    try {
      const response = await axios.post(forgotPasswordAPI, { username });

      thunkAPI.dispatch(
        setSnackbar({
          type: SnackbarType.Info,
          message: `Email to ${username} has been sent`,
        }),
      );

      return response.data;
    } catch (e: any) {
      thunkAPI.dispatch(
        setSnackbar({
          type: SnackbarType.Error,
          message: e?.response?.data?.message || 'Something went wrong',
        }),
      );

      throw e;
    }
  },
);

export const resetUserPassword = createAsyncThunk(
  `${authStoreKey}/resetUserPassword`,
  async (
    { password, token }: { password: string; token: string },
    thunkAPI,
  ) => {
    try {
      const response = await axios.post<ILoginResponse>(resetPasswordAPI, {
        password,
        token,
      });

      await signInWithEmailAndPassword(
        auth,
        response.data.profile.email,
        password,
      );

      logEvent('clients_loggedin', { 'login type': 'login/password' });

      return response.data.profile;
    } catch (e: any) {
      thunkAPI.dispatch(
        setSnackbar({
          type: SnackbarType.Error,
          message: e?.response?.data?.message || 'Something went wrong',
        }),
      );

      throw e;
    }
  },
);

export const fetchProfile = createAsyncThunk(
  `${authStoreKey}/fetchProfile`,
  async (_, thunkAPI) => {
    try {
      const response = await axios.get<IProfile>(userProfileAPI);

      return response.data;
    } catch (e: any) {
      if (e.response?.data?.statusCode === 401) {
        thunkAPI.dispatch(
          setSnackbar({
            type: SnackbarType.Error,
            message: 'Please login before accessing the app',
          }),
        );
      } else {
        thunkAPI.dispatch(
          setSnackbar({
            type: SnackbarType.Error,
            message:
              'Something went wrong with authentication, please contact developers',
          }),
        );
      }

      throw e;
    }
  },
);
